// ============================================================================
// EL GUION POR SECCIONES (T-ENT-GUION-1 · P2) — va DEBAJO del guion de siempre.
//
// Guarda en `chatai.guion_secciones` (migr 0088): título, contenido, canal (chat /
// llamada / ambos), orden y si está activa. ⭐ T-ENT-GUION-2: en cuanto hay ≥1 sección ACTIVA
// de canal chat/ambos, el CHAT arma su guion con ellas (`bloqueDesdeSecciones`, worker) y el
// texto de arriba queda de solo lectura. Sin secciones, todo sigue como antes.
// ⛔ Escribe SOLO Victorix (RLS igual que `prompts_bot`); el resto mira.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ListOrdered, Plus } from "lucide-react";
import { Aviso, Boton, Interruptor } from "./piezas.tsx";
import { Guion } from "./Guion.tsx";
import { seccionesEnUso } from "./regla_guion.ts";
import { crearSeccionGuion, explicarError, guardarSeccionGuion, leerSeccionesGuion } from "./datos.ts";
import type { CanalSeccion, ContextoPanel, SeccionGuion } from "./tipos.ts";

export const AVISO_SECCIONES = "Las secciones empiezan a usarse cuando se mude el guion (próximo paso).";
export const AVISO_SECCIONES_EN_USO = "El chat arma su guion con las secciones activas de canal Chat o Ambos, en este orden.";

export { seccionesEnUso };
const CANALES: ReadonlyArray<{ valor: CanalSeccion; etiqueta: string }> = [
  { valor: "chat", etiqueta: "Chat" }, { valor: "llamada", etiqueta: "Llamada" }, { valor: "ambos", etiqueta: "Ambos" },
];

export function GuionSecciones({ ctx, alLeer }: { ctx: ContextoPanel; alLeer?: (s: SeccionGuion[]) => void }) {
  const { sb, tenantId, rol } = ctx;
  const puedeEditar = rol === "victorix";
  const [secciones, setSecciones] = useState<SeccionGuion[]>([]);
  const [borradores, setBorradores] = useState<Record<string, Partial<SeccionGuion>>>({});
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    try { const s = await leerSeccionesGuion(sb, tenantId); setSecciones(s); alLeer?.(s); setBorradores({}); setError(null); }
    catch (e) { setError(explicarError(e)); }
  }, [sb, tenantId, alLeer]);
  useEffect(() => { void recargar(); }, [recargar]);
  const enUso = seccionesEnUso(secciones);

  async function correr(fn: () => Promise<unknown>) {
    if (ocupado) return;
    setOcupado(true); setError(null);
    try { await fn(); await recargar(); } catch (e) { setError(explicarError(e)); } finally { setOcupado(false); }
  }

  const valor = (s: SeccionGuion) => ({ ...s, ...borradores[s.id] });
  const tocar = (id: string, c: Partial<SeccionGuion>) => setBorradores((b) => ({ ...b, [id]: { ...b[id], ...c } }));

  const anadir = () => correr(() => crearSeccionGuion(sb, tenantId, {
    titulo: "Sección nueva", contenido: "", canal: "ambos",
    orden: secciones.length ? Math.max(...secciones.map((s) => s.orden)) + 1 : 0,
  }));
  const guardar = (s: SeccionGuion) => {
    const b = borradores[s.id] ?? {};
    if (b.titulo !== undefined && !b.titulo.trim()) { setError("Cada sección necesita un título."); return; }
    return correr(() => guardarSeccionGuion(sb, s.id, {
      ...(b.titulo !== undefined ? { titulo: b.titulo.trim() } : {}),
      ...(b.contenido !== undefined ? { contenido: b.contenido } : {}),
      ...(b.canal !== undefined ? { canal: b.canal } : {}),
    }));
  };
  /** Subir/bajar = intercambiar el `orden` con la vecina (dos escrituras, lo que quedó se relee). */
  const mover = (i: number, d: -1 | 1) => {
    const a = secciones[i], b = secciones[i + d];
    if (!a || !b) return;
    const oa = a.orden === b.orden ? i : a.orden, ob = a.orden === b.orden ? i + d : b.orden;
    return correr(async () => { await guardarSeccionGuion(sb, a.id, { orden: ob }); await guardarSeccionGuion(sb, b.id, { orden: oa }); });
  };

  return (
    <section data-bloque="secciones-nuevas" className="border border-borde rounded-xl bg-superficie p-4 space-y-3">
      <h2 className="text-[14px] font-semibold flex items-center gap-2">
        <span className="text-tinta-2"><ListOrdered size={15} /></span>
        El guion por secciones
      </h2>
      <Aviso tono={enUso ? "ok" : "alerta"}>
        {enUso
          ? <span data-aviso-secciones="en-uso">{AVISO_SECCIONES_EN_USO}</span>
          : <><span data-aviso-secciones>{AVISO_SECCIONES}</span> Mientras tanto, el bot usa el texto de arriba.</>}
        {!puedeEditar && <> Estas secciones las escribe Victorix por ahora.</>}
      </Aviso>
      {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
      {secciones.length === 0 && (
        <p className="text-[12.5px] text-tinta-2 px-1">Todavía no hay secciones.</p>
      )}
      <ol className="space-y-3">
        {secciones.map((s0, i) => {
          const s = valor(s0);
          const cambiada = !!borradores[s0.id];
          if (!puedeEditar) return (
            <li key={s0.id} data-seccion-guion={s0.id} data-activa={s0.activo ? "si" : "no"}
              className="border border-borde-suave rounded-lg p-3">
              <p className="text-[13px] font-semibold">{s0.titulo}
                <span className="ml-2 text-[11.5px] font-normal text-tinta-2">
                  {CANALES.find((c) => c.valor === s0.canal)?.etiqueta}{s0.activo ? "" : " · apagada"}
                </span>
              </p>
              <p className="text-[13px] mt-1 whitespace-pre-wrap leading-relaxed">{s0.contenido}</p>
            </li>
          );
          return (
            <li key={s0.id} data-seccion-guion={s0.id} data-activa={s0.activo ? "si" : "no"}
              className="border border-borde-suave rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input value={s.titulo} disabled={!puedeEditar} maxLength={120} aria-label="Título de la sección"
                  onChange={(e) => tocar(s0.id, { titulo: e.target.value })}
                  className="flex-1 min-w-0 h-9 px-3 rounded-lg bg-elevado text-tinta text-sm font-medium border border-borde
                    focus:outline-none focus:ring-2 focus:ring-marca/40 disabled:opacity-60" />
                {puedeEditar && (<>
                  <Boton tam="sm" tono="neutro" disabled={ocupado || i === 0} onClick={() => mover(i, -1)} aria-label="Subir"><ArrowUp size={14} /></Boton>
                  <Boton tam="sm" tono="neutro" disabled={ocupado || i === secciones.length - 1} onClick={() => mover(i, 1)} aria-label="Bajar"><ArrowDown size={14} /></Boton>
                </>)}
              </div>
              <textarea value={s.contenido} disabled={!puedeEditar} maxLength={8000} rows={4} aria-label="Contenido de la sección"
                onChange={(e) => tocar(s0.id, { contenido: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-elevado text-tinta text-sm border border-borde
                  focus:outline-none focus:ring-2 focus:ring-marca/40 disabled:opacity-60" />
              <div className="flex flex-wrap items-center gap-3">
                <div role="radiogroup" aria-label="Canal" className="inline-flex gap-0.5 p-1 rounded-lg bg-elevado border border-borde-suave">
                  {CANALES.map((c) => (
                    <button key={c.valor} type="button" role="radio" aria-checked={s.canal === c.valor} disabled={!puedeEditar}
                      data-canal={c.valor} onClick={() => tocar(s0.id, { canal: c.valor })}
                      className={`h-7 px-3 rounded-md text-[12.5px] font-medium ${s.canal === c.valor
                        ? "bg-superficie text-tinta shadow-[var(--shadow-tarjeta)]" : "text-tinta-2"} disabled:cursor-not-allowed`}>
                      {c.etiqueta}
                    </button>
                  ))}
                </div>
                <div className="flex-1 min-w-[160px]">
                  <Interruptor activo={s0.activo} deshabilitado={!puedeEditar || ocupado} testId={`activa_${s0.id}`}
                    etiqueta="Activa" onCambiar={(v) => correr(() => guardarSeccionGuion(sb, s0.id, { activo: v }))} />
                </div>
                {puedeEditar && cambiada && (
                  <Boton tam="sm" tono="marca" disabled={ocupado} onClick={() => guardar(s0)}>Guardar sección</Boton>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {puedeEditar && (
        <Boton tono="neutro" disabled={ocupado} onClick={anadir}>
          <Plus size={14} className="mr-1.5" /> Añadir sección
        </Boton>
      )}
    </section>
  );
}

/** La pestaña «Guion» del entrenamiento: el guion de siempre y, debajo, las secciones. */
export function GuionConSecciones({ ctx }: { ctx: ContextoPanel }) {
  // T-ENT-GUION-2 (P4): las secciones las lee el editor de abajo UNA vez y avisa aquí.
  const [enUso, setEnUso] = useState(false);
  const alLeer = useCallback((s: SeccionGuion[]) => setEnUso(seccionesEnUso(s)), []);
  return <Guion ctx={ctx} reemplazado={enUso} debajo={<GuionSecciones ctx={ctx} alLeer={alLeer} />} />;
}
